/** @jsx jsx */

import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Icon } from '@zapier/zinnia-icons';
import {
  Colors,
  Margin,
  Shadows,
  SkeletonText,
  Spacer,
  Text,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import Divider from 'app/developer-v3/components/Divider';
import Link from 'app/common/components/Link';
import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import OAuthErrorMessage from 'app/developer-v3/components/OAuthErrorMessage';
import SaveButtonGroup from 'app/developer-v3/components/SaveButtonGroup';
import InputEditor, {
  InputValue,
} from 'app/developer-v3/components/InputEditor';
import { PartnerUserInteractionEventTracker } from 'app/developer-v3/teams/team-partner-sharing/createInteractionTracker';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { useNotify, useOAuthApp } from 'app/developer-v3/hooks';
import { getCSRFToken } from 'app/developer-v3/hooks/useCSRFToken';
import { PARTNER_SOLUTIONS_BASE_DOC_URL } from 'app/developer-v3/constants';

const styleContainer = css`
  display: flex;
  gap: 25px;
  margin-top: 40px;
`;

const styleCard = css`
  display: flex;
  flex-direction: column;
  padding: 25px 40px;
  border: 1px solid ${Colors.GrayWarm3};
  border-radius: 8px;
  box-shadow: ${Shadows.elevation5};
`;

const styleLink = css`
  display: flex;
  align-self: flex-start;
  gap: 5px;
  align-items: center;
  font-weight: 700;
`;

type Props = {
  appId: ID;
  slug: string;
  trackInteraction: PartnerUserInteractionEventTracker;
};

function Domains({ slug, trackInteraction }: Props) {
  const oauthAppResult = useOAuthApp({ slug });
  const notify = useNotify();

  const [domains, setDomains] = useState<InputValue[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  async function saveDomains() {
    if (oauthAppResult.type !== 'ok') {
      throw new Error('Failed to save domains');
    }

    const eventId = uuidv4();
    const correlationId = uuidv4();

    // This event was caused by itself, so the causationId is the same as eventId
    const causationId = eventId;

    trackInteraction({
      eventAction: 'click',
      interactionName: 'save_domain_allowlist',
      interactionGoal: 'Update domain allowlist for OAuth or iframes',
      eventId,
      correlationId,
      causationId,
    });

    setIsSaving(true);
    const nonEmptyDomains = domains
      .map(({ value }) => value)
      .filter(value => value);
    const url = new URL(
      `/api/v4/oauth/applications/${oauthAppResult.data.id}/`,
      ZAPIER_APP_BASE_URL
    );
    const response = await fetch(url.href, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': await getCSRFToken(),
      },
      method: 'PATCH',
      body: JSON.stringify({
        correlation_id: correlationId,
        causation_id: eventId,
        allowlist_domains: nonEmptyDomains,
      }),
    });
    const json = await response.json();

    if (json.errors) {
      console.error(json.errors);
      notify.failure('Unable to update domains');
      if (json.errors[0].detail.allowlist_domains) {
        setDomains(
          nonEmptyDomains.map((domain, i) => {
            if (json.errors[0].detail.allowlist_domains[i]) {
              return { value: domain, isInvalidValue: true };
            }
            return { value: domain, isInvalidValue: false };
          })
        );
      }
    } else {
      setDomains(json.allowlist_domains.map(value => ({ value })));
      notify.success('Domains updated!');
    }

    setIsSaving(false);
  }

  useEffect(() => {
    if (oauthAppResult.type === 'ok') {
      setDomains(
        oauthAppResult.data.allowlistDomains.map(value => ({ value }))
      );
    }
  }, [oauthAppResult]);

  if (oauthAppResult.type === 'loading') {
    return (
      <Margin margin="30px 45px">
        <SkeletonText lines={5} size="heading" />
      </Margin>
    );
  }

  if (oauthAppResult.type === 'fail') {
    return <OAuthErrorMessage margin="30px 45px" />;
  }

  return (
    <ManagerLayout title="Embedding Domains">
      <Text margin="-10px 0 20px" tag="p">
        If you want to embed the Workflow Element, provide us with a list of
        domains you intend to embed it in and we&apos;ll permit these specific
        domains. This embed uses iframes, which are blocked by default due to
        security.{' '}
        <Link
          href={`${PARTNER_SOLUTIONS_BASE_DOC_URL}/elements-security`}
          style={{ textDecoration: 'underline' }}
          target="_blank"
        >
          Learn more about this in the embed FAQs.
        </Link>
      </Text>
      <Text margin="15px 0 10px" tag="h2" type="Body3">
        Domain examples
      </Text>
      <Text margin="0 15px 20px" tag="ul">
        <li>www.company.com</li>
        <li>
          company.com{' '}
          <em>
            (www.company.com won&apos;t be allowed simply by adding company.com)
          </em>
        </li>
        <li>staging.company.com</li>
        <li>*.company.com</li>
      </Text>
      <Text margin="15px 0" tag="p">
        Note: any path after “.com” will be ignored.
      </Text>
      <InputEditor
        inputName="Enter domain"
        items={domains}
        onUpdate={setDomains}
      />
      <Spacer height="20px" />
      <Divider />
      <SaveButtonGroup isSaving={isSaving} onSave={saveDomains} />
      <div css={styleContainer}>
        <div css={styleCard}>
          <Text tag="h2" type="Body3">
            Workflow Element
          </Text>
          <Text margin="10px 0" tag="p">
            The Workflow Element lets you pull all of Zapier's embed solutions
            into your product—including the Zap Editor—with just three lines of
            code. No API required. This means that your users can now discover,
            create, and edit their Zaps inside your app, helping them accomplish
            what they set out to do: build workflows without distractions.
          </Text>
          <Link
            css={styleLink}
            href="https://zapier.com/partner/embed/workflow"
            iconPosition="end"
            target="_blank"
          >
            Get started
            <Icon name="arrowOffsite" size={16} />
          </Link>
        </div>
      </div>
    </ManagerLayout>
  );
}

export default Domains;
